import React from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid2,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useTheme } from "@mui/material/styles";

const worshipHighlights = [
  {
    title: "Sunday Worship",
    description: "Experience the joy of collective worship and fellowship every Sunday.",
    timing: "9:00 AM - 11:30 AM",
  },
  {
    title: "Midweek Evening Prayer",
    description: "A time of midweek reflection and spiritual rejuvenation.",
    timing: "Wednesday, 7:00 PM - 8:00 PM",
  },
  {
    title: "Fasting Prayer",
    description: "A special time to seek God’s presence through fasting and prayer.",
    timing: "Saturday, 10:00 AM - 1:00 PM",
  },
];

const detailedSchedule = [
  {
    day: "Sunday",
    timings: [
      { service: "Children's Class", time: "8:00 AM - 9:00 AM" },
      { service: "General Worship", time: "9:00 AM - 11:30 AM" },
      { service: "Night Worship", time: "7:00 PM - 8:00 PM" },
    ],
  },
  {
    day: "Tuesday",
    timings: [{ service: "Women's Prayer", time: "10:00 AM - 12:00 PM" },],
  },
  {
    day: "Wednesday",
    timings: [
      { service: "House Fellowship", time: "7:00 PM - 8:30 PM" },
    ],
  },
  {
    day: "Friday",
    timings: [{ service: "Youth Prayer", time: "7:00 PM - 8:00 PM" }],
  },
  {
    day: "Saturday",
    timings: [
      { service: "Fasting Prayer", time: "10:00 AM - 1:00 PM" },
      { service: "Evening Prayer", time: "7:00 PM - 8:00 PM" },
      { service: "Song Practice", time: "8:00 PM - 9:00 PM" },
    ],
  },
];

const WorshipServices = () => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        py: 6,
        backgroundColor: "background.default",
        animation: `${theme.keyframes.fadeIn} 1.5s`,
        width: "100%",
      }}
    >
      {/* Helmet for SEO */}
      <Helmet>
        <title>Worship Services | Praise Revival Church Dharmapuri</title>
      </Helmet>
      <Container
        maxWidth="lg" // This will set the maximum width for the container to ensure equal margins
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center", // Centers the content horizontally
          width: "100%",
          paddingLeft: "16px",
          paddingRight: "16px",
        }}
      >
        {/* Page Title */}
        <Typography
          variant="h2"
          gutterBottom
          align="center"
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            animation: `${theme.keyframes.fadeInUp} 1.5s`,
            width: "100%",
          }}
        >
          Worship Services
        </Typography>

        {/* Key Worship Highlights Section */}
        <Box sx={{ mb: 6, width: "100%" }}>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{
              color: "primary.main",
              animation: `${theme.keyframes.fadeInUp} 2s`,
            }}
          >
            Key Worship Highlights
          </Typography>
          <Grid2 container spacing={4} justifyContent="center">
            {worshipHighlights.map((highlight, index) => (
              <Grid2
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{
                  animation: `${theme.keyframes.zoomIn} 2s`,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Card
                  elevation={3}
                  sx={{
                    textAlign: "center",
                    p: 3,
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                    height: "auto",
                    width: "100%",
                    maxWidth: "400px", // Ensure max width for the cards
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    boxShadow: theme.shadows[3],
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: theme.shadows[5],
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      color: "primary.main",
                    }}
                  >
                    {highlight.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{
                      color: "text.primary",
                    }}
                  >
                    {highlight.description}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontStyle: "italic",
                      color: "secondary.main",
                    }}
                  >
                    {highlight.timing}
                  </Typography>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        </Box>

        {/* Weekly Service Schedule Section */}
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{
              color: "primary.main",
              mb: 3,
              animation: `${theme.keyframes.fadeInUp} 1.5s`,
            }}
          >
            Weekly Service Schedule
          </Typography>
          <Grid2 container spacing={4} justifyContent="center">
            {detailedSchedule.map((daySchedule, index) => (
              <Grid2
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{
                  animation: `${theme.keyframes.fadeIn} 2s`,
                }}
              >
                <Card
                  elevation={2}
                  sx={{
                    backgroundColor: "background.paper",
                    p: 2,
                    borderRadius: 2,
                    height: "auto",
                    width: "100%",
                    maxWidth: "400px", // Max width for large screens
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    boxShadow: theme.shadows[2],
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: theme.shadows[4],
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color: "secondary.main",
                      }}
                    >
                      {daySchedule.day}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {daySchedule.timings.map((timing, idx) => (
                      <Typography
                        key={idx}
                        variant="body1"
                        sx={{
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        <strong>{timing.service}</strong>: {timing.time}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        </Box>
      </Container>
    </Box>
  );
};

export default WorshipServices;
